/**
 * @file companyNavConfig.ts
 * @brief Navigation tree for the Company Admin mega menu.
 *
 * @author DES
 * @date 19-06-2026
 */

import {
  Building,
  AudioLines,
  CalendarClock,
  CalendarDays,
  ChartColumn,
  FileChartColumn,
  House,
  Mic,
  PenLine,
  Receipt,
  Settings,
  History,
  ToggleRight,
  UsersRound,
} from "lucide-react";
import { BoardroomTable } from "@/components/icons/BoardroomTable";
import { MedicalReport } from "@/components/icons/MedicalReport";
import { SavedCards } from "@/components/icons/SavedCards";
import { SessionTurns } from "@/components/icons/SessionTurns";
import { TodoTick } from "@/components/icons/TodoTick";
import { UsageRing } from "@/components/icons/UsageRing";
import { COMPANY_REPORT_CATALOG } from "@/features/company-reports/reportCatalog";
import type { NavShellConfig } from "./nav-types";

const companyReportNavChildren = [
  {
    id: "reports-hub",
    to: "/company/reports",
    label: "All reports",
    description: "Browse the full report catalog",
    icon: FileChartColumn,
    end: true as const,
  },
  ...COMPANY_REPORT_CATALOG.filter((r) => r.available).map((report) => ({
    id: `report-${report.id}`,
    to: `/company/reports/${report.id}`,
    label: report.title,
    description: report.description,
    icon: report.icon,
  })),
];

/** Per-company product toggles (from GET /api/company/features). */
export type NavFeatureAccess = {
  interpretation?: boolean;
  notes?: boolean;
  boardroom?: boolean;
  medical_assessment?: boolean;
};

/**
 * Nav routes that require a company product to be ON. These are product-ACTION
 * surfaces (scheduling interpretation appointments for the team) — pointless
 * when the company has the product off, so they're hidden even for the admin.
 * The admin can still USE interpretation and review history/oversight
 * (Sessions, Meeting Intel, Medical Assessment), which is why those are
 * intentionally NOT here. (15-07-2026)
 */
const ROUTE_REQUIRES_FEATURE: Record<string, keyof NavFeatureAccess> = {
  "/company/schedule": "interpretation",
  "/company/interpret": "interpretation",
};

/** Extra signals that gate a nav route beyond the feature toggles. */
export type NavContext = {
  featureAccess?: NavFeatureAccess;
  /** Platform kill switches — true means locked OFF system-wide. */
  platformLocks?: Partial<Record<keyof NavFeatureAccess, boolean>>;
  /** Whether the company has any interpretation session (Sessions link). */
  hasSessions?: boolean;
  /** Live interpretation on the web: the pilot list. Only `true` shows it. */
  webInterpret?: boolean;
};

/** Platform kill switch — product surfaces hidden when Verblizr locks a module. */
const PLATFORM_LOCKED_ROUTE_PREFIXES: Array<{
  prefix: string;
  feature: keyof NavFeatureAccess;
}> = [
  { prefix: "/company/sessions", feature: "interpretation" },
  { prefix: "/company/schedule", feature: "interpretation" },
  { prefix: "/company/interpret", feature: "interpretation" },
  { prefix: "/company/notes", feature: "notes" },
  { prefix: "/company/meetings", feature: "boardroom" },
  { prefix: "/company/todos", feature: "boardroom" },
  { prefix: "/company/medical-assessment", feature: "medical_assessment" },
];

/** A route is shown unless a signal explicitly rules it out. Unknown signals
 *  (still loading — undefined) always show, so the nav never flash-hides. */
function routeAllowed(to: string | undefined, ctx: NavContext): boolean {
  if (!to) return true;
  // Sessions link only when the company actually has interpretation sessions.
  if (to === "/company/sessions" && ctx.hasSessions === false) return false;
  // Live interpretation is a pilot (01-10-2026): hidden unless listed.
  if (to === "/company/interpret" && ctx.webInterpret !== true) return false;
  for (const rule of PLATFORM_LOCKED_ROUTE_PREFIXES) {
    if (
      (to === rule.prefix || to.startsWith(`${rule.prefix}/`)) &&
      ctx.platformLocks?.[rule.feature] === true
    ) {
      return false;
    }
  }
  // Product-action routes need their product on.
  const required = ROUTE_REQUIRES_FEATURE[to];
  if (required && ctx.featureAccess && ctx.featureAccess[required] === false) {
    return false;
  }
  return true;
}

export function buildCompanyNavConfig(
  subtitle?: string,
  featureAccess?: NavFeatureAccess,
  hasSessions?: boolean,
  platformLocks?: NavContext["platformLocks"],
  webInterpret?: boolean,
): NavShellConfig {
  const ctx: NavContext = { featureAccess, hasSessions, platformLocks, webInterpret };
  const config: NavShellConfig = {
    homeTo: "/company/home",
    scopeTitle: "Company Admin",
    scopeSubtitle: subtitle,
    /* Quick access is a 3-column grid, so each row is one family:
       overview (Home, Dashboard, Analytics), Meeting Intel, sessions, then
       the rest (Medical Assessment, Invoices, Reports). (30-09-2026) */
    highlights: [
      // The phone's Home, first, as in the app's menu (30-09-2026).
      {
        to: "/company/home",
        label: "Home",
        description: "Recent meetings, sessions and notes",
        icon: House,
        end: true,
      },
      {
        to: "/company",
        label: "Dashboard",
        description: "Overview, KPIs, and recent activity",
        icon: UsageRing, // DES 08-10-2026: app icon, was LayoutDashboard
        end: true,
      },
      {
        to: "/company/analytics",
        label: "Analytics",
        description: "Personal vs corporate sessions, spend, and languages",
        icon: ChartColumn,
      },
      {
        to: "/company/meetings",
        label: "Meeting Intel",
        description: "Summaries, action items, and speaker analytics",
        icon: BoardroomTable, // DES 08-10-2026: app icon, was Presentation
      },
      {
        to: "/company/meetings/new",
        label: "New boardroom",
        description: "Record a meeting from this laptop",
        icon: Mic,
      },
      {
        to: "/company/meetings/scheduled",
        label: "Scheduled Meetings",
        description: "Scheduled boardrooms · Start now",
        icon: CalendarClock,
      },
      {
        to: "/company/interpret",
        label: "Interpreter",
        description: "Interpret a conversation from this laptop",
        icon: AudioLines,
      },
      {
        to: "/company/sessions",
        label: "Sessions",
        description: "Browse transcripts, costs, and downloads",
        icon: SessionTurns, // DES 08-10-2026: app icon, was Activity
      },
      {
        to: "/company/schedule",
        label: "Diary",
        description: "Book and manage upcoming appointments",
        icon: CalendarDays,
      },
      /* The admin's OWN notes, not the company's. Deliberately NOT in
         ROUTE_REQUIRES_FEATURE: that hides product-ACTION surfaces for the
         TEAM when a company toggles a product off, and an admin's own notes
         are not the team's. (21-08-2026) */
      {
        to: "/company/notes",
        label: "Notes",
        description: "Write a meeting agenda · your own notes",
        icon: PenLine, // DES 08-10-2026: app icon, was NotebookPen
      },
      {
        to: "/company/medical-assessment",
        label: "Medical Assessment",
        description: "Case Hub, medico-legal assessments, GRIP export",
        icon: MedicalReport, // DES 08-10-2026: app icon, was FileHeart
      },
      {
        to: "/company/invoices",
        label: "Invoices",
        description: "Payment history, PDFs, and exports",
        icon: Receipt,
      },
      {
        to: "/company/reports",
        label: "Reports",
        description: "Exportable usage, spend, Meeting Intel, and pool burn",
        icon: FileChartColumn,
      },
    ],
    /* Browse is grouped by PRODUCT MODULE, the same split as the Features
       toggles (Boardroom / Interpretation / Medical Assessment), then the
       cross-module and account groups. Each route appears once. A module
       the platform locks drops its whole group via routeAllowed. (30-09-2026) */
    groups: [
      {
        id: "boardroom",
        label: "Boardroom",
        items: [
          {
            to: "/company/meetings",
            label: "Meeting Intel",
            description: "Summaries, action items, and speaker analytics",
            icon: BoardroomTable, // DES 08-10-2026: app icon, was Presentation
          },
          {
            to: "/company/meetings/new",
            label: "New boardroom",
            description: "Record a meeting from this laptop",
            icon: Mic,
          },
          {
            to: "/company/meetings/scheduled",
            label: "Scheduled Meetings",
            description: "Scheduled boardrooms · Start now",
            icon: CalendarClock,
          },
          {
            to: "/company/notes",
            label: "Notes",
            description: "Write a meeting agenda · your own notes",
            icon: PenLine, // DES 08-10-2026: app icon, was NotebookPen
          },
          // The admin's OWN to-dos from every meeting (12-10-2026). Browse
          // only: Quick access is a 3-column grid of whole families.
          {
            to: "/company/todos",
            label: "My to-dos",
            description: "Open, overdue and done, from all your meetings",
            icon: TodoTick,
          },
        ],
      },
      {
        id: "interpretation",
        label: "Interpretation",
        items: [
          {
            to: "/company/interpret",
            label: "Interpreter",
            description: "Interpret a conversation from this laptop",
            icon: AudioLines,
          },
          {
            to: "/company/sessions",
            label: "Sessions",
            description: "Browse transcripts, costs, and downloads",
            icon: SessionTurns, // DES 08-10-2026: app icon, was Activity
          },
          {
            to: "/company/schedule",
            label: "Diary",
            description: "Book, edit and cancel sessions",
            icon: CalendarDays,
          },
        ],
      },
      {
        id: "medical",
        label: "Medical Assessment",
        items: [
          {
            to: "/company/medical-assessment",
            label: "Case Hub",
            description: "Case Hub, medico-legal assessments, GRIP export",
            icon: MedicalReport, // DES 08-10-2026: app icon, was FileHeart
          },
        ],
      },
      {
        id: "insights",
        label: "Insights",
        items: [
          {
            to: "/company/analytics",
            label: "Analytics",
            description: "Personal vs corporate sessions, spend, and languages",
            icon: ChartColumn,
          },
          {
            to: "/company/reports",
            label: "Reports",
            description: "Exportable usage, spend, Meeting Intel, pool burn",
            icon: FileChartColumn,
            children: companyReportNavChildren,
          },
        ],
      },
      {
        id: "people",
        label: "People",
        items: [
          {
            to: "/company/employees",
            label: "Employees",
            description: "Invite and manage your team",
            icon: UsersRound,
          },
          {
            to: "/company/activity",
            label: "Activity log",
            description: "Who deleted or changed what",
            icon: History,
          },
        ],
      },
      {
        id: "billing",
        label: "Billing",
        items: [
          {
            to: "/company/invoices",
            label: "Invoices",
            description: "Payment history, PDFs, and exports",
            icon: Receipt,
          },
          {
            to: "/company/billing",
            // "Billing" (DES 21-10-2026): the page holds the plan too.
            label: "Billing",
            description: "Your plan and corporate cards",
            icon: SavedCards, // DES 08-10-2026: app icon, was CreditCard
          },
        ],
      },
      {
        id: "company",
        label: "Company",
        items: [
          {
            to: "/company/profile",
            label: "Company profile",
            icon: Building,
          },
          {
            to: "/company/features",
            label: "Features",
            description: "Enable or disable products for your team",
            icon: ToggleRight,
          },
          {
            to: "/company/settings",
            label: "Settings",
            icon: Settings,
          },
        ],
      },
    ],
  };

  // Hide product-action routes (interpretation scheduling) when the company has
  // that product off. Groups that end up empty are dropped entirely.
  return {
    ...config,
    highlights: config.highlights.filter((h) => routeAllowed(h.to, ctx)),
    groups: config.groups
      .map((g) => ({
        ...g,
        items: g.items.filter((i) => routeAllowed(i.to, ctx)),
      }))
      .filter((g) => g.items.length > 0),
  };
}
